import { useContext, useMemo } from 'react';
import { Button, useDisclosure } from '@ds';
import { AuthContext } from '@lp/core/auth';
import { AssignedUser, Case, CaseStatus, CaseUserGroup, ServiceType } from '@/api/apis/case';
import { CaseDetailsPermissions } from '@/permissions';
import { isAllowedStatus } from '@/utils/helpers';
import { TxCenterContacts } from './components/Sections/TxCenterContacts';
import { OpoContacts } from './components/Sections/OpoContacts';
import { CaseManagement } from './components/Sections/CaseManagement';
import { ClinicalResources } from './components/Sections/ClinicalResources';
import { AssignResourcesModal } from './components/AssignResourcesModal/AssignResourcesModal';
import { LogisticsManagement } from './components/Sections/LogisticsManagement';
import { SetIsPrimaryModal } from './components/SetIsPrimaryModal';
import { useSetIsPrimaryUser } from './hooks/useSetIsPrimaryUser';
import { primaryAvailableGroups } from '@/constants/user.constants';

export const AssignedResourcesTab = ({ caseInfo }: { caseInfo: Case }) => {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const {
    isOpen: isSetPrimaryModalOpen,
    onOpenChange: onSetPrimaryModalOpen,
    onClose: onSetPrimaryModalClose,
  } = useDisclosure();
  const { checkPermission } = useContext(AuthContext);
  const hasAssignedResourcesUpdatePermission = checkPermission(CaseDetailsPermissions.AssignedResourcesUpdate);
  const {
    txCenterDetails,
    assignedOpoContacts,
    opoDetails,
    assignedEmployees = {},
    status,
    caseId,
    assignedCustomers = {},
    serviceType,
    logisticsReference,
  } = caseInfo;
  const { handleSetIsPrimaryUser, isPendingSetIsPrimaryUser } = useSetIsPrimaryUser({ caseId });
  const txCenterId = txCenterDetails?.id;
  const canAssignInCurrentStatus =
    status && isAllowedStatus({ currentStatus: status, statusTo: CaseStatus.ReturningToBase });
  const isEditingEnabled =
    hasAssignedResourcesUpdatePermission &&
    canAssignInCurrentStatus &&
    caseInfo.serviceType !== ServiceType.DirectAcquisition;

  const primaryAvailableUsers = useMemo(() => {
    return Object.entries(assignedEmployees).reduce(
      (acc, [key, value]) => {
        const isPrimaryAvailableGroup = primaryAvailableGroups.includes(key as CaseUserGroup);

        if (isPrimaryAvailableGroup && value.length > 0) {
          acc[key as CaseUserGroup] = value;
        }

        return acc;
      },
      {} as Partial<Record<CaseUserGroup, AssignedUser[]>>,
    );
  }, [assignedEmployees]);

  return (
    <div>
      <div className="flex justify-between mb-8">
        <h1 className="text-hm!">Case resources</h1>
        {isEditingEnabled && (
          <Button className="h-8 text-s-sb!" onClick={onOpen}>
            Assign New
          </Button>
        )}
      </div>
      {isOpen && (
        <AssignResourcesModal
          onClose={onClose}
          onOpenChange={onOpenChange}
          caseInfo={caseInfo}
          onSetPrimaryModalOpen={onSetPrimaryModalOpen}
        />
      )}
      {isSetPrimaryModalOpen && (
        <SetIsPrimaryModal
          isPending={isPendingSetIsPrimaryUser}
          isOpen={isSetPrimaryModalOpen}
          onOpenChange={onSetPrimaryModalOpen}
          onSubmit={handleSetIsPrimaryUser}
          primaryAvailableUsers={primaryAvailableUsers}
          caseId={caseId}
          onClose={onSetPrimaryModalClose}
        />
      )}
      {txCenterId && serviceType !== ServiceType.DirectAcquisition && (
        <TxCenterContacts
          menuDisabled={!isEditingEnabled}
          txCenter={txCenterDetails}
          assignedCustomers={assignedCustomers}
          caseId={caseId}
        />
      )}
      <ClinicalResources caseId={caseId} />
      {!!opoDetails && (
        <OpoContacts
          menuDisabled={!isEditingEnabled}
          opo={opoDetails}
          opoContacts={assignedOpoContacts}
          caseId={caseId}
        />
      )}
      <CaseManagement menuDisabled={!isEditingEnabled} assignedEmployees={assignedEmployees} caseId={caseId} />
      <LogisticsManagement
        menuDisabled={!isEditingEnabled}
        assignedEmployees={assignedEmployees}
        logisticsId={logisticsReference?.id}
        caseId={caseId}
      />
    </div>
  );
};
